import { IUserDto, IUserInfoDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import * as React from "react";
import { syncRememberedAccountPhoto } from "../../../auth/remembered-accounts";
import { UserService } from "../../../services/user-service";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaAvatar, { getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaRoleBadge from "../../ui-kit/kuika-role-badge/kuika-role-badge";
import { formatMemberSince, IUserInfoWithCreatedAt } from "./profile-member-since";

export interface IState {
  isHoveringAvatar: boolean;
  profilePhoto: string;
  pendingPhotoFile: File | null;
  /**
   * Removal is staged like a new photo is: the dialog commits on Update, so Back has to
   * leave the stored photo alone.
   */
  isPhotoRemovalPending: boolean;
}

export interface IProps {
  profileInfo: IUserInfoDto;
  openChangePwdModal: () => void;
  updateUserInfo: () => void;
  /**
   * The `id` the dialog's footer submits. The commit button sits in the modal's footer
   * slot, which is a sibling of this form rather than a descendant, so the association is
   * the HTML `form` attribute and not nesting.
   */
  formId: string;
  /**
   * The footer owns the button, so the dialog is told where the submit stands. `false` means
   * a submit is already running or its ✓ is still up — this one does not go out.
   */
  onSubmitStart: () => boolean;
  /** The update landed. The dialog holds its ✓ on Update, then closes itself. */
  onSubmitSuccess: () => void;
  onSubmitFailure: () => void;
}

declare let window: any;

class ProfileUserUpdate extends React.PureComponent<IProps & IWithContextProp, IState> {
  constructor(props: IProps & IWithContextProp) {
    super(props);
    this.state = {
      isHoveringAvatar: false,
      profilePhoto: "",
      pendingPhotoFile: null,
      isPhotoRemovalPending: false
    };
  }

  /** The photo the account actually has on the server, as opposed to a local preview. */
  private storedPhotoUrl: string = "";

  componentDidMount() {
    this.storedPhotoUrl = this.props.context.profilePhoto || this.props.profileInfo.profilePhotoUrl || "";
    this.setState({ profilePhoto: this.storedPhotoUrl });
  }

  onFinish = async (values: any) => {
    if (!this.props.profileInfo?.id || !this.props.onSubmitStart()) return;
    try {
      const obj = {} as IUserDto;
      obj.username = values.userName;
      obj.firstName = values.firstName;
      obj.lastName = values.lastName;
      obj.id = this.props.profileInfo.id;

      if (this.state.pendingPhotoFile) {
        const formData = new FormData();
        formData.append("file", this.state.pendingPhotoFile);
        await this.handleProfilePhotoUpload(formData);
      } else if (this.state.isPhotoRemovalPending) {
        await this.handleProfilePhotoRemoval();
      }

      await UserService.Update(this.props.profileInfo.id, obj);
      this.props.onSubmitSuccess();
    } catch (error) {
      console.error("Error updating user:", error);
      this.props.onSubmitFailure();
      KMainFunctions.displayErrorNotification("An error occurred while updating user");
    } finally {
      this.setState({ pendingPhotoFile: null, isPhotoRemovalPending: false });
      this.props.updateUserInfo();
    }
  };

  private currentPhotoPreviewUrl: string | undefined;

  componentWillUnmount(): void {
    if (this.currentPhotoPreviewUrl) {
      URL.revokeObjectURL(this.currentPhotoPreviewUrl);
      this.currentPhotoPreviewUrl = undefined;
    }
  }

  setHoveringAvatar = (isHover: boolean) => {
    this.setState({ isHoveringAvatar: isHover });
  };

  handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    this.setPhotoPreview(file);
  };

  setPhotoPreview = (file: File) => {
    // Revoke the previous preview URL before creating a new one so repeatedly
    // picking photos doesn't leak Blobs; the final one is revoked on unmount.
    if (this.currentPhotoPreviewUrl) URL.revokeObjectURL(this.currentPhotoPreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    this.currentPhotoPreviewUrl = previewUrl;
    this.setState({
      profilePhoto: previewUrl,
      pendingPhotoFile: file,
      isHoveringAvatar: false,
      isPhotoRemovalPending: false
    });
  };

  removePhoto = (): void => {
    if (this.currentPhotoPreviewUrl) {
      URL.revokeObjectURL(this.currentPhotoPreviewUrl);
      this.currentPhotoPreviewUrl = undefined;
    }
    this.setState({
      profilePhoto: "",
      pendingPhotoFile: null,
      // Dropping a preview that was never sent is not something the server has to be told
      // about; only a stored photo is.
      isPhotoRemovalPending: Boolean(this.storedPhotoUrl),
      isHoveringAvatar: false
    });
  };

  handleProfilePhotoUpload = async (formData: FormData) => {
    const res = await UserService.UpdateProfilePhoto(this.props.profileInfo.id, formData);
    if (res && res.status === 200) {
      this.props.context.setProfilePhoto(res.data.url);
      // The Team table reads this global for its avatar column (users.tsx).
      window.profilePhoto = res.data.url;
      this.storedPhotoUrl = res.data.url;
      // So the sign-in page's "Welcome back" greets with the new photo after a sign-out.
      syncRememberedAccountPhoto(this.props.profileInfo.username, res.data.url);
      this.setState({ profilePhoto: res.data.url });
    } else {
      throw new Error("Profile photo update failed");
    }
  };

  handleProfilePhotoRemoval = async (): Promise<void> => {
    const isRemoved: boolean = await UserService.RemoveProfilePhoto(this.props.profileInfo.id);
    if (!isRemoved) {
      throw new Error("Profile photo removal failed");
    }
    this.props.context.setProfilePhoto("");
    // Every avatar reads this global BEFORE the account it was given, so leaving the old
    // url here would put the photo straight back on the header and the Team table.
    window.profilePhoto = "";
    this.storedPhotoUrl = "";
    syncRememberedAccountPhoto(this.props.profileInfo.username, "");
    this.setState({ profilePhoto: "" });
  };

  handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * The ring is a hover affordance, so a drag that wanders off has to put it away again —
   * without this the photo keeps a blue ring around it for the rest of the session.
   * `dragleave` also fires on the way INTO a child, which is why the move is only counted
   * when it genuinely left the label.
   */
  handleDragLeave = (event: React.DragEvent<HTMLElement>): void => {
    const nextTarget: Node | null = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    this.setHoveringAvatar(false);
  };

  handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Before the early returns below: the drag is over either way, so the ring goes with
    // it even when the file is refused.
    this.setHoveringAvatar(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      KMainFunctions.displayErrorNotification("Please upload a valid image file (jpg, jpeg, or png)");
      return;
    }

    this.setPhotoPreview(file);
  };

  render() {
    const fullName: string = `${this.props.profileInfo?.firstName ?? ""} ${
      this.props.profileInfo?.lastName ?? ""
    }`.trim();
    const memberSince: string = formatMemberSince((this.props.profileInfo as IUserInfoWithCreatedAt)?.createdAt);
    const hasPhoto: boolean = Boolean(this.state.profilePhoto);
    return (
      <Form
        id={this.props.formId}
        name="user"
        initialValues={{
          remember: true,
          email: this.props.profileInfo.username,
          firstName: this.props.profileInfo.firstName,
          lastName: this.props.profileInfo.lastName
        }}
        onFinish={this.onFinish}
      >
        <div className="kuika_profile__user-update">
          <div className="user-update__body">
            {/* The photo IS the identity header: a large avatar with the name, the
                  address, the join date and Change photo beside it, the way an account page
                  states who you are. It replaces the rail, which had nothing else to hold. */}
            <div className="user-update__identity">
              <label
                htmlFor="fileUpload"
                className={`identity__photo${this.state.isHoveringAvatar ? " is-dragging" : ""}`}
                onMouseEnter={() => this.setHoveringAvatar(true)}
                onMouseLeave={() => this.setHoveringAvatar(false)}
                onDragEnter={() => this.setHoveringAvatar(true)}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
              >
                <KuikaAvatar
                  data-testid="myProfileModalUserPhoto"
                  size={120}
                  src={this.state.profilePhoto}
                  // The id first: a colour tied to the display name changed when the
                  // person did.
                  seed={getIdentitySeed(this.props.profileInfo)}
                >
                  {KMainFunctions.getUserLettersToShow(this.props.profileInfo)}
                </KuikaAvatar>
              </label>
              <div className="identity__text">
                <div className="identity__head">
                  <span className="identity__name" title={fullName}>
                    {fullName || this.props.profileInfo?.username}
                  </span>
                  {/* The rail stated the role; it must not vanish just because the form is
                        open. Not editable here — this dialog cannot change a workspace role. */}
                  {this.props.profileInfo?.workspaceRoles?.map((role) => (
                    <KuikaRoleBadge key={role.enumId} role={role.name} />
                  ))}
                </div>
                <div className="identity__meta">
                  <span
                    data-testid="myProfileModalEmail"
                    className="identity__mail"
                    title={this.props.profileInfo?.username}
                  >
                    {this.props.profileInfo?.username}
                  </span>
                  {memberSince && (
                    <>
                      <span className="identity__dot" />
                      <span className="identity__since">Member since {memberSince}</span>
                    </>
                  )}
                </div>
                <div className="identity__photo-actions">
                  <label htmlFor="fileUpload" data-testid="dragAndDropTest" className="identity__change">
                    Change photo
                  </label>
                  {/* Only when there is one to take away — an account showing its initials
                      has nothing to remove. */}
                  {hasPhoto && (
                    <button
                      type="button"
                      data-testid="myProfileModalRemovePhotoButton"
                      className="identity__remove"
                      onClick={this.removePhoto}
                    >
                      Remove photo
                    </button>
                  )}
                </div>
                <span className="identity__note">JPG or PNG, or drop one on the picture.</span>
              </div>
            </div>
            {/* The avatar and Change photo both point at it. */}
            <input
              type="file"
              id="fileUpload"
              style={{ display: "none" }}
              onChange={this.handleFileChange}
              accept=".jpg,.png,.jpeg"
            />
            {/* No Email field: it was permanently disabled, so it is a fact, and the
                  header above states it. */}
            <div className="user-update__fields">
              <span className="user-update__label">Name</span>
              <Form.Item name="firstName" rules={[{ required: true, message: "Please input your name!" }]}>
                <KuikaInput data-testid="myProfileModalNameInput" />
              </Form.Item>
              <span className="user-update__label">Surname</span>
              <Form.Item name="lastName" rules={[{ required: true, message: "Please input your name!" }]}>
                <KuikaInput data-testid="myProfileModalSurnameInput" />
              </Form.Item>
            </div>
            {/* The third thing this dialog edits, stated like the other two: a labelled
                  control under the fields, not a link floating over the footer. It opens a
                  dialog rather than taking a value, so it is a button and not an input. */}
            <div className="user-update__password">
              <span className="user-update__label">Password</span>
              <button
                type="button"
                data-testid="myProfileModalChangePasswordButton"
                className="user-update__change-pass"
                onClick={this.props.openChangePwdModal}
              >
                Change password
              </button>
            </div>
          </div>
          {/* Delete account, Back and Update used to be drawn here. They are decisions, so
                they belong in the dialog's footer slot — where they stay put while the form
                scrolls and take the same shape as every other dialog's. */}
        </div>
      </Form>
    );
  }
}

const profileUserUpdate = withContext<IProps>(ProfileUserUpdate);
export { profileUserUpdate as ProfileUserUpdate };
